import React, { useState } from "react";

import { toUpper } from "lodash/fp";
import { Navigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import { Button, FormControlLabel, Stack, Typography } from "@mui/material";

import TextField from "@app/analytics/components/TextField";
import useAuth from "@app/common/auth/useAuth";
import { Link } from "@app/common/components/Link";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isWorkspaceOwner } from "@app/common/workspaces/workspaceUtils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Checkbox from "@rui/Checkbox";
import InfoPanel from "@rui/InfoPanel";

const CONFIRMATION_TEXT = "DELETE";

const WorkspaceDelete = () => {
  usePageTitle("Delete Workspace");

  const { user } = useAuth();
  const { deleteWorkspace, workspace, inWorkspace } = useWorkspace();

  const handleDeleteWorkspace = () => deleteWorkspace.mutate();

  const [textFieldValue, setTextFieldValue] = useState("");
  const [dataDeleteAcknowledged, setDataDeleteAcknowledged] = useState(false);

  // redirect anyone who doesn't belong here
  if (!inWorkspace) {
    return <Navigate to={ROUTES.USER_SETTINGS_DEFAULT_SECTION} replace />;
  }

  if (!isWorkspaceOwner(user?.workspace)) {
    return <Navigate to={ROUTES.workspaceSettingsById(workspace.id)} replace />;
  }

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTextFieldValue(e.target.value);

  return (
    <Stack flexGrow={1} sx={{ bgcolor: "#FFF", p: 5 }}>
      <Stack
        spacing={3}
        sx={{
          margin: "auto",
          mt: "40px",
          maxWidth: "670px",
          color: "shades.neutral.500",
        }}
      >
        <Stack spacing={1}>
          <Typography variant="h2" sx={{ color: "shades.neutral.800" }}>
            You are deleting {workspace.name} Workspace
          </Typography>
          <Typography>
            This action is permanent and cannot be undone.
          </Typography>
        </Stack>
        <InfoPanel severity="info">
          <Typography variant="bodySecondaryReg" color="shades.primary.600">
            Important! If you wish to leave this Workspace without deleting it,
            you can{" "}
            <Link to={ROUTES.TRANSFER_WORKSPACE} underline color="inherit">
              Transfer Ownership
            </Link>{" "}
            to another Workspace member.
          </Typography>
        </InfoPanel>
        <Typography variant="bodyPrimaryReg">
          To continue with Workspace deletion, please type “{CONFIRMATION_TEXT}”
          below.
        </Typography>
        <TextField
          InputProps={{ placeholder: CONFIRMATION_TEXT }}
          sx={{ maxWidth: "450px", "& input": { textTransform: "uppercase" } }}
          onChange={handleTextFieldChange}
        />
        <FormControlLabel
          control={
            <Checkbox
              sx={{ mr: 1 }}
              onChange={(_e, checked) => setDataDeleteAcknowledged(checked)}
            />
          }
          label={
            <Typography variant="bodyPrimaryReg" color="shades.neutral.500">
              I understand that all meeting data and insights will be deleted
              for this Workspace.
            </Typography>
          }
        />
        <Stack direction="row" sx={{ height: "46px" }} spacing={2}>
          <LoadingButton
            variant="contained"
            color="error"
            disabled={
              toUpper(textFieldValue) !== CONFIRMATION_TEXT ||
              !dataDeleteAcknowledged
            }
            loading={deleteWorkspace.isPending}
            onClick={handleDeleteWorkspace}
          >
            Delete Workspace
          </LoadingButton>
          <Button
            sx={{ width: "70px" }}
            component={Link}
            to={ROUTES.workspaceSettingsSettingsById(workspace.id)}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default WorkspaceDelete;
